import { useMemo } from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import { useGetLatestMeshDocumentsQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const useLatestMeshDocuments = ({
  organizationId,
  forceNetworkFetch = false,
}: {
  organizationId: string;
  forceNetworkFetch?: boolean;
}): {
  latestMeshDocuments: DataPipeline.MeshDocument[];
  loading: boolean;
  error?: Error;
} => {
  const flags = useFeatureFlags();

  // this hook queries latest mesh documents and is reused in multiple places from the
  // page, some place even on timer (when polling for recent mesh runs).
  // its important to fetch fresh from network when user opens the drawer to upload
  // against latest graph. But otherwise we can used cached data.
  // So let the caller decide the fetch policy.
  const fetchPolicy = forceNetworkFetch ? 'network-only' : 'cache-first';

  const { data, loading, error } = useGetLatestMeshDocumentsQuery({
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
    },
    fetchPolicy,
  });

  const latestMeshDocuments = useMemo(() => {
    return (
      data?.latestMeshDocuments.map((meshDocumentStr) => {
        return DbGraphUtils.deserializeMeshDocument(meshDocumentStr);
      }) || []
    );
  }, [data?.latestMeshDocuments]);

  return {
    latestMeshDocuments,
    loading,
    error,
  };
};
